import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import utils from './features/utils';

import { AuthSignIn, AXIOS, AXIOS_STATUS, AxiosService, LanguageService, LoggerService } from '@umanforge/common';
import { UserUmanforge } from 'interfaces/user.interfaces';
import { Sso } from 'interfaces/sso.interfaces';
import { saveToken, saveUser } from '@umanforge/common';

import { API } from './constants/api.constants';

import { Copyright } from './components/copyright.component';
import { ErrorSso } from './components/error_sso.component';
import { ForgotPassword } from './components/forgot_password.component';
import { LanguageSelector } from './components/language_selector.component';
import { Login } from './components/login.component';
import { NeedChangePassword } from './components/change_password.component';
import { ToastService } from '@common_services/toast.service';
import { Loading } from 'components/loading.component';
import { UmanforgeLogo } from 'components/logos/umanforge_logo.component';
import { VERSION } from 'constants/app.constants';


const logger: LoggerService = new LoggerService( 'App' );
const toastService: ToastService = new ToastService( 'App' );

export enum AppStates {
  LoadingState = 'loading',
  LoginState = 'login',
  ForgotPasswordState = 'forgot_password',
  ErrorSsoState = 'error_sso',
  NeedChangePasswordState = 'need_change_password',
  ErrorServer = 'error_server',
}

export function App() {

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [ state, setState ] = useState<AppStates>( AppStates.LoadingState );
  const [ errorSso, setErrorSso ] = useState<string>( '' );

  const getAuthSignIn = (): Promise<AxiosResponse<AuthSignIn, unknown> | null> => {
    return new Promise( resolve => {
      AXIOS.get( API.IS_SIGN_IN )
        .then( ( response: AxiosResponse ) => {
          resolve( response );
        } )
        .catch( ( err: AxiosError ) => {
          if ( err.status === AXIOS_STATUS.FAILURE || err.code === AXIOS_STATUS.ERR_NETWORK ) {
            setState( AppStates.ErrorServer );
          } else {
            resolve( null );
          }
        } );
    } );
  };

  const changePassword = ( password: string ): Promise<boolean> => {
    return new Promise( async resolve => {
      const result = await getAuthSignIn();
      if ( result && result.data.signin && result.data.user ) {
        const token = ( result.headers as AxiosHeaders ).get( 'auth-token' ) as string;
        AXIOS.put( API.USER_CHANGE_PASSWORD.replace( '{userId}', result.data.user.id ), { password }, { headers: { 'auth-token': token } } )
          .then( ( response: AxiosResponse ) => {
            if ( response.status === 202 ) {
              toastService.showWarning( AxiosService.getAxiosMessage( response ) );
            }
            resolve( response.status === 201 );
          } )
          .catch( err => {
            utils.onAxiosError( err, toastService, setState );
            resolve( false );
          } );
      } else {
        toastService.showWarning( t( 'An error has occurred, please reconnect with the temporary password !' ) );
        setState( AppStates.LoginState );
        resolve( false );
      }
    } );
  };

  useEffect( () => {
    logger.verbose( 'Version :', VERSION );
  }, [] );


  useEffect( () => {
    getAuthSignIn()
      .then( resultSignIn => {
        if ( resultSignIn && resultSignIn.data.signin && resultSignIn.data.user ) {
          const user = resultSignIn.data.user as UserUmanforge;
          if ( user.needChangePassword ) {
            setState( AppStates.NeedChangePasswordState );
          } else {
            utils.connectUser( ( resultSignIn.headers as AxiosHeaders ).get( 'auth-token' ) as string );
          }
        } else {
          const code = ( new URLSearchParams( window.location.search ) ).get( 'code' );
          const redirectUrl = ( new URLSearchParams( window.location.search ) ).get( 'redirect_url' );
          const headers = { headers: { 'redirect-url': redirectUrl } };
          if ( !code || code === '' ) {
            AXIOS.get( API.GET_SSO, headers )
              .then( ( response: AxiosResponse ) => {
                if ( response.status === 200 ) {
                  const result = response.data as Sso;
                  if ( result.enable ) {
                    window.location.replace( result.urlAuth );
                  } else {
                    setState( AppStates.LoginState );
                  }
                } else {
                  setState( AppStates.LoginState );
                }
              } )
              .catch( ( err: AxiosError ) => {
                utils.onAxiosError( err, toastService, setState );
              } );
          } else {
            AXIOS.post( API.SIGN_IN_SSO, { code } )
              .then( ( response: AxiosResponse ) => {
                if ( response.status === 200 ) {
                  const result = response.data as AuthSignIn;
                  if ( result.signin ) {
                    utils.connectUser( ( response.headers as AxiosHeaders ).get( 'auth-token' ) as string );
                  }
                } else if ( response.status === 202 ) {
                  setErrorSso( response.data );
                  setState( AppStates.ErrorSsoState );
                }
              } )
              .catch( ( err: AxiosError ) => {
                if ( err.status === AXIOS_STATUS.FAILURE || err.code === AXIOS_STATUS.ERR_NETWORK ) {
                  setState( AppStates.ErrorServer );
                } else {
                  let message: string = AxiosService.getAxiosMessage( err );
                  toastService.showError( message );
                  if ( typeof err.response?.data === 'string' ) {
                    message = String( err.response.data );
                  }
                  setErrorSso( message );
                  setState( AppStates.ErrorSsoState );
                }
              } );
          }
        }
      } );
  }, [] );

  useEffect( () => {
    const checkUserConnection = async () => {
      try {
        const response = await AXIOS.get( API.IS_SIGN_IN );
        if ( response.status === 200 && response.data.signin ) {
          const user = response.data.user as UserUmanforge;
          const token = ( response.headers as AxiosHeaders ).get( 'auth-token' ) as string;

          if ( user.needChangePassword ) {
            setState( AppStates.NeedChangePasswordState );
          } else {
            dispatch( saveToken( token ) );
            dispatch( saveUser( user ) );
            utils.connectUser( token );
          }
        }
      } catch {
        // comportement normal
      }
    };

    const handleVisibilityChange = () => {
      if ( document.visibilityState === 'visible' ) {
        checkUserConnection();
      }
    };

    document.addEventListener( 'visibilitychange', handleVisibilityChange );

    return () => {
      document.removeEventListener( 'visibilitychange', handleVisibilityChange );
    };
  }, [ dispatch ] );

  const renderStates = (): ReactElement => {
    let element: ReactElement | null = null;
    switch ( state ) {
    case AppStates.LoginState:
      element = (
        <Login
          onClickForgotPassword={ () => { setState( AppStates.ForgotPasswordState ); } }
          navigateChangePassword={ () => { setState( AppStates.NeedChangePasswordState ); } }
          onAxiosError={ err => {
            utils.onAxiosError( err, toastService, setState );
          } }
        />
      );
      break;
    case AppStates.ForgotPasswordState:
      element = (
        <ForgotPassword
          onClickBackLogin={ () => { setState( AppStates.LoginState ); } }
          onAxiosError={ err => {
            utils.onAxiosError( err, toastService, setState );
          } }
        />
      );
      break;
    case AppStates.ErrorSsoState:
      element = (
        <ErrorSso error={ errorSso } />
      );
      break;
    case AppStates.NeedChangePasswordState:
      element = (
        <NeedChangePassword
          title={ t( 'Welcome' ) }
          subtitle={ t( 'Your current password is a temporary password. Please change it.' ) }
          buttonText={ t( 'Save password' ) }
          onClick={ changePassword }
          onAxiosError={ err => {
            utils.onAxiosError( err, toastService, setState );
          } }
        />
      );
      break;
    case AppStates.LoadingState:
      element = (
        <div className='flex h-full items-center justify-center'>
          <Loading size={150}/>
        </div>
      );
      break;
    case AppStates.ErrorServer:
      element = (
        <div className='flex flex-col items-center justify-center h-full gap-4 p-6 pb-12'>
          <div className='flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 shadow-md p-2.5'>
            <svg className='fill-red-500' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              />
            </svg>

          </div>
          <div className='flex flex-col'>
            <span className='text-[18px] text-gray-800'>{ t( 'The server is not responding' ) }</span>
            <span className='text-[16px] text-gray-700'>{ t( 'It does not appear to be accessible' ) }</span>
          </div>
        </div>
      );
      break;
    default:
      element = <p></p>;
      break;
    }
    return (
      <div className='flex min-h-[400px] w-fit max-w-[850px] min-w-[680px] py-4 rounded-md shadow-gray-200 shadow-xl bg-white'>
        <div className='h-full w-1/2 p-4'>
          <div className='flex flex-col w-full h-full gap-4 text-center justify-center items-center rounded-xl'>
            <UmanforgeLogo className='w-[200px] h-fit fill-primary'/>
            <div className='flex flex-col'>
              <span className='font-umanforge text-6xl text-neutral-900'> { t( 'common:apps.umanforge' ) } </span>
              <span className='font-umanforge text-2xl text-neutral-600'> { t( 'common:industrial_operations_suite' ) } </span>
            </div>
          </div>
        </div>
        <div className='flex w-[4px] my-[2%] rounded-full bg-light'/>
        <div className='h-full w-1/2 p-4'>
          { element }
        </div>
      </div>
    );
  };

  return (
    <div className='flex w-screen h-screen justify-center items-center bg-light overflow-hidden pb-[70px]'>

      <div className='flex flex-col gap-5 h-full items-center'>
        <span className='text-center text-gray-900 text-6xl font-umanforge h-full content-center pt-20'>
          { t( 'Welcome to the forge !' ) }
        </span>
        <div className='flex'>
          { renderStates() }
        </div>
        <div className='h-full'>
        </div>
      </div>

      <footer className='absolute bottom-0 z-30 w-full bg-white border-t border-gray-300 flex items-center justify-between py-2 px-4'>
        <div className='text-center'>
          <Copyright />
        </div>
        <div className='flex justify-end'>
          <LanguageSelector
            menuPlacement='top'
            onChange={ newLang => {
              LanguageService.changeLanguage( newLang.value );
            } }
          />
        </div>
      </footer>

    </div>
  );
}